import { ColDef, ColGroupDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useRef } from "react";

import { aggridHeaderClass } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import {
	CreateCellClickHandler,
	createCellCssClassRules,
	CustomCellClickAction,
	GridCellConfig,
	useRowSelectionHandler,
} from "src/utils/grid/GridHandlerContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef } from "src/utils/grid/gridStyleUtils";

import {
	TQC2090_R01Dto,
	TQC2090_R01_VALUE_FIELDS,
} from "./Dto_TQC2090_R01";
import { useTQC2090_R01Store } from "./Store_TQC2090_R01";
import { Tab_TQC2090_R01 } from "./Tab_TQC2090_R01";
import {
	getTQC2090_R01ColumnDefinitions,
	getTQC2090_R01EditableColumns,
	getTQC2090_R01GridOptions,
	normalizeTQC2090_R01Headers,
	TQC2090_R01_HEADER_NAMES,
	TQC2090_R01_READONLY_COLUMNS,
} from "./components/ColumnDefinitions";
import { useTQC2090_R01CellChange } from "./hooks/TQC2090_R01CellChange";
import { getTQC2090_R01ColumnStateKey } from "./utils/tabConfig";

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<TQC2090_R01Dto> | null>;
	defaultTQC2090_R01Row: TQC2090_R01Dto;
	onChangeLine: (workplaceCd: string) => void;
}

export const Table_TQC2090_R01 = (props: Props) => {
	const { clickable, setClickable, gridRef, defaultTQC2090_R01Row, onChangeLine } = props;
	const {
		TQC2090_R01List,
		setTQC2090_R01List,
		updateTQC2090_R01List,
		originalTQC2090_R01List,
		selectedTQC2090_R01List,
		addSelectedTQC2090_R01List,
		removeSelectedTQC2090_R01List,
		initSelectedTQC2090_R01List,
		allSelectedTQC2090_R01List,
		columnState,
		setColumnState,
		scrollTop,
		setScrollTop,
		TQC2090_R01Filter,
	} = useTQC2090_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const isNickelLine = TQC2090_R01Filter.workplaceCd === "CR";
	const columnStateKey = getTQC2090_R01ColumnStateKey(TQC2090_R01Filter.workplaceCd);

	const editableColumns = useMemo(
		() => getTQC2090_R01EditableColumns(isNickelLine),
		[isNickelLine],
	);

	const headerNames = useMemo(() => ({ ...TQC2090_R01_HEADER_NAMES }), []);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: "valueIndex",
		gridData: TQC2090_R01List,
		defaultGridData: defaultTQC2090_R01Row,
		selectedRow: selectedTQC2090_R01List,
		originalGridData: originalTQC2090_R01List,
		updateRow: (index: number, data: TQC2090_R01Dto) => updateTQC2090_R01List(index, data),
	});

	const cssCellConfig: GridCellConfig = useMemo(() => ({
		disabledCells: [],
		insertedDisabledCells: [],
		commonDisabledCells: TQC2090_R01_READONLY_COLUMNS,
		pointerCells: editableColumns,
		insertedPointerCells: [],
		commonPointerCells: [],
		alignLeftCells: ["inspItemNm", "specText", "thicknessItemNm", "thicknessSpecText", "remark",],
	}), [editableColumns]);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<TQC2090_R01Dto>({
			idKey: "valueIndex",
			selectedList: selectedTQC2090_R01List,
			deletedList: [],
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		createCellClassRules, selectedTQC2090_R01List,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns, alignLeftColumns,
	]);

	const baseColumnDefs = useMemo(
		() => getTQC2090_R01ColumnDefinitions(isNickelLine),
		[isNickelLine],
	);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs as ColDef<TQC2090_R01Dto>[], [])
	, [baseColumnDefs]);

	const activeColumnState = useMemo(() => {
		const validColumnIds = new Set([
			"rowIndex", "dayOfMonth", "dayOfWeekNm", "inspItemNm",
			...(isNickelLine
				? [
					"inspItemNm", "specText",
					"inspectValue1_1", "inspectValue2_1", "inspectValue3_1",
					"thicknessItemNm", "thicknessSpecText",
					"thicknessFirstValue", "thicknessMiddleValue", "thicknessLastValue",
					"remark",
				]
				: [...TQC2090_R01_VALUE_FIELDS, "remark"]),
		]);
		const filteredState = columnState?.filter((state) => validColumnIds.has(state.colId)) ?? null;
		if (!filteredState || !isNickelLine) {
			return filteredState;
		}
		const remarkState = filteredState.find((state) => state.colId === "remark")
			?? defaultColumnState.find((state) => state.colId === "remark");
		if (!remarkState) return filteredState;

		const stateWithoutRemark = filteredState.filter((state) => state.colId !== "remark");
		const dayOfWeekIndex = stateWithoutRemark.findIndex((state) => state.colId === "dayOfWeekNm");
		const insertIndex = dayOfWeekIndex >= 0 ? dayOfWeekIndex + 1 : stateWithoutRemark.length;
		return [
			...stateWithoutRemark.slice(0, insertIndex),
			remarkState,
			...stateWithoutRemark.slice(insertIndex),
		];
	}, [columnState, defaultColumnState, isNickelLine]);

	const columnDefs: (ColDef<TQC2090_R01Dto> | ColGroupDef<TQC2090_R01Dto>)[] = useMemo(() => {
		const merged = GridCustomColumnDefs<TQC2090_R01Dto>({
			baseColumnDefs,
			columnState: activeColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
		return normalizeTQC2090_R01Headers(merged);
	}, [activeColumnState, baseColumnDefs, headerNames]);

	const { handleCellChange } = useTQC2090_R01CellChange({
		updateTQC2090_R01List,
		originalTQC2090_R01List,
	});

	const handleRowClick = useRowSelectionHandler({
		dataList: TQC2090_R01List,
		idKey: "valueIndex",
		selectedList: selectedTQC2090_R01List,
		deletedList: [],
		addSelected: addSelectedTQC2090_R01List,
		removeSelected: removeSelectedTQC2090_R01List,
		clickable, setClickable,
	});

	const cellEventHandlers = useDetailTableEvents({
		detailList: TQC2090_R01List as any,
		editableColumns,
		keyDownConfig: { enableArrowNavigation: true },
	});

	// 편집 가능 셀 클릭 시 바로 편집 시작
	const cellClickActions: CustomCellClickAction<TQC2090_R01Dto>[] = useMemo(() => [
		{
			validate: (field) => editableColumns.includes(field),
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); },
		},
	], [editableColumns]);
	const handleCellClick = CreateCellClickHandler(TQC2090_R01List, "valueIndex", cellClickActions);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: "",
		editable: false,
	}]), []);

	return (
		<div className="w-full h-full flex flex-col">
			<Tab_TQC2090_R01 onChangeLine={onChangeLine} />
			<div className="flex-1">
				<GridContextWrapper<TQC2090_R01Dto>
					enableCellCalculation
					gridWrapperRef={gridWrapperRef}
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setTQC2090_R01List}
					initSelected={initSelectedTQC2090_R01List}
					allSelected={allSelectedTQC2090_R01List}
					defaultRowData={defaultTQC2090_R01Row}
					baseColumnDefs={activeColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setColumnState}
					localStorageKey={columnStateKey}
					menuItems={undefined}
				>
					<Grid_Excel<TQC2090_R01Dto>
						key={columnStateKey}
						ref={gridRef}
						rowData={paddedGridData}
						getRowId={getRowId}
						columnDefs={columnDefs}
						defaultColDef={cellCssDefaultColDef}
						rowClassRules={createRowClassRules<TQC2090_R01Dto>({
							idKey: "valueIndex",
							selectedList: selectedTQC2090_R01List,
							deletedList: [],
						})}
						onRowClicked={handleRowClick}
						onCellClicked={handleCellClick}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRow}
						columnStateKey={columnStateKey}
						setColumnState={setColumnState}
						getDefaultColumnState={() => defaultColumnState}
						scrollTop={scrollTop}
						setScrollTop={setScrollTop}
						{...getTQC2090_R01GridOptions()}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
